import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

const DEFAULT_LEVEL = 3;

/**
 * `H` extends the HTML `h` element with additional support for changing the level dynamically.
 *
 * This is useful when creating higher order components which contains headings and you want to be able to use the component
 * with different heading levels. For instance:
 * ````
 *   <Group title="Top level group" level="2">
 *     <Group title="Subgroup" level="3">...</Group>
 *   </Group>
 * ````
 * You do not need to use this component if your heading should always be the same level. The you are better off using the
 * standard `h1`, `h2`, `h3`, `h4`, `h5`, `h6` elements.
 *
 * The component is also capable of showing a counter to the right of the title. Counter values greater than 99 will be
 * displayed as 99+.
 *
 * @property {node} children The child components to put inside the `h` element.
 * @property {string} className The class name of this component. Will be appended to "core-h".
 * @property {number} count An optional counter displayed to the right of the title. No counter is displayed by default.
 * @property {number} level The level of the title, 1 => `h1`, 2 => `h2`, etc. Defaults to level 3 (`h3`).
 *
 * @see Group
 */
export default class H extends PureComponent {

  static displayName = 'H';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    count: PropTypes.number,
    level: PropTypes.number
  };

  static defaultProps = {
    level: DEFAULT_LEVEL
  };

  render() {
    const { children, className, count, level, ...rest } = this.props;
    const el = `h${Math.min(6, Math.max(1, level || DEFAULT_LEVEL))}`;
    const content = isNaN(count) ? children : [].concat(children, <span className="count" key="1">{count > 99 ? '99+' : count}</span>);
    const props = rest ? { ...rest } : {};

    props.className = cx('core-h', className);
    return React.createElement(el, props, content);
  }

}
